import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Headphones, MessageCircle, Users, Building2, Plus, Trash2, 
  ExternalLink, Copy, Link, FileText, Check, X, Eye, Clock,
  BookOpen, Upload, RefreshCw, Settings, ChevronRight, AlertCircle,
  Bell, BellRing, AlignLeft, AlignCenter, AlignRight, Filter, Ticket,
  Send, Search, ChevronDown, ChevronUp, UserCheck, Hash, Mail, Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_LABELS = {
  low: { label: 'Baja', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  normal: { label: 'Normal', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  high: { label: 'Alta', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  urgent: { label: 'Urgente', color: 'text-red-400', bg: 'bg-red-500/20' }
};

const STATUS_LABELS = {
  open: { label: 'Abierto', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  in_progress: { label: 'En Progreso', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  waiting_user: { label: 'Esperando Resp.', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  resolved: { label: 'Resuelto', color: 'text-green-400', bg: 'bg-green-500/20' },
  closed: { label: 'Cerrado', color: 'text-gray-400', bg: 'bg-gray-500/20' }
};

const USER_TOPICS = ['cuenta', 'pagos', 'marketplace', 'calendario', 'salud', 'musica', 'viajes', 'documentos', 'bug', 'sugerencia', 'otro'];
const BUSINESS_TOPICS = ['cuenta', 'facturacion', 'configuracion_bot', 'analytics', 'integraciones', 'api', 'bug', 'sugerencia', 'otro'];

const getTopicLabel = (topic) => {
  return topic ? topic.charAt(0).toUpperCase() + topic.slice(1).replace(/_/g, ' ') : 'Sin tema';
};

// ─── Ticket Detail View ──────────────────────────────────────────────────────
const TicketDetail = ({ ticket, onBack, onStatusChange, onReply, onResolve }) => {
  const [replyText, setReplyText] = useState('');
  const [resolveText, setResolveText] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [sending, setSending] = useState(false);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    await onReply(ticket.id, replyText);
    setReplyText('');
    setSending(false);
  };

  const handleResolve = async () => {
    if (!resolveText.trim()) return;
    await onResolve(ticket.id, resolveText);
    setShowResolveForm(false);
  };

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1 transition-colors">
        ← Volver a la lista
      </button>

      {/* Ticket Header */}
      <div className="glass-panel p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-gray-500 text-sm font-mono">{ticket.ticket_number}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_LABELS[ticket.status]?.bg} ${STATUS_LABELS[ticket.status]?.color}`}>
                {STATUS_LABELS[ticket.status]?.label || ticket.status}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_LABELS[ticket.priority]?.bg} ${PRIORITY_LABELS[ticket.priority]?.color}`}>
                {PRIORITY_LABELS[ticket.priority]?.label || ticket.priority}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${ticket.user_type === 'user' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                {ticket.user_type === 'user' ? '👤 Usuario' : '🏢 Negocio'}
              </span>
            </div>
            <h3 className="text-xl text-white font-semibold">{ticket.subject}</h3>
          </div>
          <select
            value={ticket.status}
            onChange={(e) => onStatusChange(ticket.id, e.target.value)}
            className="bg-white/5 text-gray-300 rounded-lg px-3 py-2 outline-none border border-white/10 text-sm"
          >
            {Object.entries(STATUS_LABELS).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
        </div>

        {/* User / Business Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-black/20 rounded-lg p-3">
            <p className="text-gray-500 text-xs mb-1">{ticket.user_type === 'user' ? 'Usuario' : 'Negocio'}</p>
            <p className="text-white text-sm font-medium truncate">{ticket.user_name || ticket.user_id || '—'}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3">
            <p className="text-gray-500 text-xs mb-1">Email</p>
            <p className="text-white text-sm truncate">{ticket.user_email || '—'}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3">
            <p className="text-gray-500 text-xs mb-1">Tema</p>
            <p className="text-white text-sm">{getTopicLabel(ticket.topic || ticket.category)}</p>
          </div>
          <div className="bg-black/20 rounded-lg p-3">
            <p className="text-gray-500 text-xs mb-1">Fecha</p>
            <p className="text-white text-sm">{new Date(ticket.created_at).toLocaleDateString('es-MX')}</p>
          </div>
        </div>

        {/* Description */}
        <div className="bg-black/20 rounded-xl p-4 mb-4">
          <p className="text-gray-500 text-xs mb-2 font-medium uppercase tracking-wider">Descripción del problema</p>
          <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
        </div>

        {/* Source */}
        {ticket.source && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>Fuente:</span>
            <span className={`px-2 py-0.5 rounded ${ticket.source === 'chat_agent' ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
              {ticket.source === 'chat_agent' ? '🤖 Chat IA' : '📝 Formulario'}
            </span>
          </div>
        )}
      </div>

      {/* Conversation History */}
      <div className="glass-panel p-6">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <MessageCircle size={18} className="text-blue-400" />
          Historial de Conversación
          {ticket.messages && ticket.messages.length > 0 && (
            <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full">{ticket.messages.length}</span>
          )}
        </h4>
        
        <div className="space-y-3 mb-6 max-h-80 overflow-y-auto custom-scrollbar pr-2">
          {(!ticket.messages || ticket.messages.length === 0) ? (
            <p className="text-gray-500 text-sm text-center py-6">Sin mensajes en este ticket aún.</p>
          ) : (
            ticket.messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.from === 'admin' || msg.from === 'support' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] rounded-xl p-3 ${
                  msg.from === 'admin' || msg.from === 'support'
                    ? 'bg-red-500/15 border border-red-500/20'
                    : msg.from === 'system'
                    ? 'bg-yellow-500/10 border border-yellow-500/20 mx-auto max-w-full text-center'
                    : 'bg-white/5 border border-white/10'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold ${
                      msg.from === 'admin' || msg.from === 'support' ? 'text-red-400' :
                      msg.from === 'system' ? 'text-yellow-400' : 'text-blue-400'
                    }`}>
                      {msg.from === 'admin' || msg.from === 'support' ? '🛡️ Soporte Admin' :
                       msg.from === 'system' ? '⚙️ Sistema' :
                       msg.from === 'user' ? '👤 Usuario' : '🏢 Negocio'}
                    </span>
                    <span className="text-xs text-gray-600">{new Date(msg.timestamp).toLocaleString('es-MX')}</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reply Box */}
        {ticket.status !== 'closed' && (
          <div className="border-t border-white/10 pt-4">
            <p className="text-gray-500 text-xs mb-2 uppercase tracking-wider font-medium">Responder</p>
            <div className="flex gap-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Escribe tu respuesta al usuario..."
                className="input-glass flex-1 h-20 resize-none text-sm"
                onKeyDown={(e) => { if (e.ctrlKey && e.key === 'Enter') handleReply(); }}
              />
              <button
                onClick={handleReply}
                disabled={sending || !replyText.trim()}
                className="btn-primary px-4 flex flex-col items-center justify-center gap-1 disabled:opacity-50"
              >
                <Send size={16} />
                <span className="text-xs">Enviar</span>
              </button>
            </div>
            <p className="text-gray-600 text-xs mt-1">Ctrl+Enter para enviar</p>
          </div>
        )}
      </div>

      {/* Actions */}
      {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
        <div className="glass-panel p-5">
          <h4 className="text-white font-medium mb-3">Acciones</h4>
          {!showResolveForm ? (
            <div className="flex gap-3">
              <button onClick={() => setShowResolveForm(true)} className="btn-primary flex items-center gap-2">
                <Check size={16} />Marcar como Resuelto
              </button>
              <button 
                onClick={() => onStatusChange(ticket.id, 'in_progress')}
                className="btn-secondary flex items-center gap-2"
              >
                <Clock size={16} />En Progreso
              </button>
              <button 
                onClick={() => onStatusChange(ticket.id, 'waiting_user')}
                className="btn-secondary flex items-center gap-2"
              >
                <UserCheck size={16} />Esperando Usuario
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <textarea
                value={resolveText}
                onChange={(e) => setResolveText(e.target.value)}
                placeholder="Describe cómo fue resuelto el problema..."
                className="input-glass w-full h-20 resize-none text-sm"
              />
              <div className="flex gap-3">
                <button onClick={handleResolve} className="btn-primary flex items-center gap-2">
                  <Check size={16} />Confirmar Resolución
                </button>
                <button onClick={() => setShowResolveForm(false)} className="btn-secondary">Cancelar</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Tickets Panel ───────────────────────────────────────────────────────────
const TicketsCategoryPanel = ({ tickets, updateTicketStatus, setSelectedTicket, selectedTicket, loadData, onReply, onResolve }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const userTickets = tickets.filter(t => t.user_type === 'user');
  const businessTickets = tickets.filter(t => t.user_type === 'business');

  const filteredTickets = tickets.filter(t => {
    if (activeCategory !== 'all' && t.user_type !== activeCategory) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (t.subject || '').toLowerCase().includes(q) ||
             (t.user_name || '').toLowerCase().includes(q) ||
             (t.ticket_number || '').toLowerCase().includes(q) ||
             (t.description || '').toLowerCase().includes(q);
    }
    return true;
  });

  if (selectedTicket) {
    return (
      <TicketDetail
        ticket={selectedTicket}
        onBack={() => setSelectedTicket(null)}
        onStatusChange={async (id, status) => {
          await updateTicketStatus(id, status);
          setSelectedTicket(prev => prev ? { ...prev, status } : null);
        }}
        onReply={onReply}
        onResolve={onResolve}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white">Tickets de Soporte</h2>
        <button onClick={loadData} className="btn-secondary flex items-center gap-2 text-sm">
          <RefreshCw size={14} />Actualizar
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'all', label: 'Todos', count: tickets.length, icon: Ticket },
          { id: 'user', label: 'Usuarios', count: userTickets.length, icon: Users, color: 'text-blue-400' },
          { id: 'business', label: 'Negocios', count: businessTickets.length, icon: Building2, color: 'text-purple-400' }
        ].map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-xl flex items-center gap-2 text-sm transition-colors ${
                activeCategory === cat.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Icon size={15} className={activeCategory !== cat.id ? cat.color : ''} />
              {cat.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeCategory === cat.id ? 'bg-white/20' : 'bg-white/10'}`}>
                {cat.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por asunto, usuario o número..."
            className="input-glass pl-8 text-sm w-full"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-glass text-sm">
          <option value="all">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="input-glass text-sm">
          <option value="all">Todas las prioridades</option>
          {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <Ticket size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">Sin tickets que coincidan con los filtros</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map(ticket => (
            <div
              key={ticket.id}
              onClick={() => setSelectedTicket(ticket)}
              className="glass-panel p-4 cursor-pointer hover:bg-white/5 transition-all hover:border-white/20"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-gray-500 text-xs font-mono">{ticket.ticket_number}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${STATUS_LABELS[ticket.status]?.bg} ${STATUS_LABELS[ticket.status]?.color}`}>
                      {STATUS_LABELS[ticket.status]?.label}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${PRIORITY_LABELS[ticket.priority]?.bg} ${PRIORITY_LABELS[ticket.priority]?.color}`}>
                      {PRIORITY_LABELS[ticket.priority]?.label}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${ticket.user_type === 'user' ? 'bg-blue-500/15 text-blue-400' : 'bg-purple-500/15 text-purple-400'}`}>
                      {ticket.user_type === 'user' ? '👤 Usuario' : '🏢 Negocio'}
                    </span>
                  </div>
                  <h4 className="text-white font-medium text-sm">{ticket.subject}</h4>
                  <p className="text-gray-500 text-xs mt-1 truncate">{ticket.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-gray-500 text-xs">{new Date(ticket.created_at).toLocaleDateString('es-MX')}</p>
                  <p className="text-gray-600 text-xs mt-1">{ticket.user_name || ticket.user_id || '—'}</p>
                  {ticket.messages?.length > 0 && (
                    <span className="text-xs text-blue-400 mt-1 block">{ticket.messages.length} msgs</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Conversations Panel ─────────────────────────────────────────────────────
const ConversationsPanel = ({ conversations, selectedConversation, setSelectedConversation }) => {
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = conversations.filter(c => {
    if (typeFilter !== 'all' && c.user_type !== typeFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (c.user_name || '').toLowerCase().includes(q) ||
             (c.user_id || '').toLowerCase().includes(q);
    }
    return true;
  });

  const userCount = conversations.filter(c => c.user_type === 'user').length;
  const businessCount = conversations.filter(c => c.user_type === 'business').length;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white">Conversaciones</h2>
        <div className="flex gap-2 text-sm">
          <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full">{userCount} usuarios</span>
          <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full">{businessCount} negocios</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'all', label: 'Todas', count: conversations.length },
          { id: 'user', label: 'Usuarios', count: userCount },
          { id: 'business', label: 'Negocios', count: businessCount }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setTypeFilter(f.id)}
            className={`px-4 py-2 rounded-xl text-sm flex items-center gap-2 transition-colors ${
              typeFilter === f.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {f.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${typeFilter === f.id ? 'bg-white/20' : 'bg-white/10'}`}>{f.count}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar conversaciones..."
          className="input-glass pl-8 text-sm w-full"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* List */}
        <div className="lg:col-span-2 space-y-2 max-h-[520px] overflow-y-auto custom-scrollbar pr-1">
          {filtered.length === 0 && (
            <div className="text-center py-10">
              <MessageCircle size={28} className="text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Sin conversaciones</p>
            </div>
          )}
          {filtered.map(conv => {
            const lastMsg = conv.messages?.[conv.messages.length - 1];
            return (
              <div
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                className={`p-4 rounded-xl cursor-pointer transition-all border ${
                  selectedConversation?.id === conv.id
                    ? 'border-red-500/50 bg-red-500/5'
                    : 'border-white/5 bg-white/3 hover:bg-white/5 hover:border-white/15'
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-white font-medium text-sm">{conv.user_name || conv.user_id || 'Usuario'}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${conv.user_type === 'user' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                    {conv.user_type === 'user' ? '👤' : '🏢'}
                  </span>
                </div>
                {lastMsg && (
                  <p className="text-gray-500 text-xs truncate">
                    <span className={lastMsg.role === 'assistant' ? 'text-red-400' : 'text-gray-400'}>
                      {lastMsg.role === 'assistant' ? '🤖' : '👤'}
                    </span>{' '}
                    {lastMsg.content}
                  </p>
                )}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-gray-600 text-xs">{conv.messages?.length || 0} mensajes</span>
                  <span className="text-gray-600 text-xs">{new Date(conv.updated_at || conv.created_at).toLocaleDateString('es-MX')}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Conversation Detail */}
        <div className="lg:col-span-3">
          {selectedConversation ? (
            <div className="glass-panel flex flex-col h-[520px]">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium">{selectedConversation.user_name || 'Usuario'}</h4>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {selectedConversation.user_type === 'user' ? '👤 Usuario' : '🏢 Negocio'} •{' '}
                    {selectedConversation.messages?.length || 0} mensajes •{' '}
                    {new Date(selectedConversation.updated_at || selectedConversation.created_at).toLocaleString('es-MX')}
                  </p>
                </div>
                <button onClick={() => setSelectedConversation(null)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X size={16} className="text-gray-400" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                {(selectedConversation.messages || []).map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-xl text-sm ${
                      msg.role === 'user'
                        ? 'bg-blue-500/20 text-white ml-8'
                        : 'bg-white/5 text-gray-300 mr-8'
                    }`}>
                      <p className="leading-relaxed">{msg.content}</p>
                      {msg.timestamp && (
                        <p className="text-xs opacity-50 mt-1">{new Date(msg.timestamp).toLocaleTimeString('es-MX')}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="glass-panel h-[520px] flex items-center justify-center">
              <div className="text-center">
                <MessageCircle size={40} className="text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">Selecciona una conversación para verla</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const AdminSupportPanel = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newTicketCount, setNewTicketCount] = useState(0);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [newKnowledge, setNewKnowledge] = useState({ name: '', source_type: 'url', content: '', agent_type: 'user' });

  useEffect(() => {
    loadData();
    checkNotificationPermission();
    const interval = setInterval(checkNewTickets, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkNotificationPermission = () => {
    if ('Notification' in window) setNotificationsEnabled(Notification.permission === 'granted');
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) { toast.error('Tu navegador no soporta notificaciones'); return; }
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notificaciones activadas');
        new Notification('OkBot Admin', { body: 'Recibirás alertas de nuevos tickets', icon: '/okbot_logo_2026.png' });
      }
    } catch (e) { console.error(e); }
  };

  const checkNewTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/unnotified`);
      const unnotified = res.data || [];
      if (unnotified.length > 0) {
        setNewTicketCount(unnotified.length);
        if (notificationsEnabled && Notification.permission === 'granted') {
          unnotified.forEach(ticket => {
            new Notification('🎫 Nuevo Ticket de Soporte', {
              body: `${ticket.subject}\n${ticket.user_type === 'user' ? 'Usuario' : 'Negocio'}: ${ticket.ticket_number}`,
              icon: '/okbot_logo_2026.png', tag: ticket.id
            });
          });
        }
        await axios.put(`${API}/tickets/mark-notified`, unnotified.map(t => t.id));
        loadData();
      }
    } catch (e) { console.error(e); }
  }, [notificationsEnabled]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, ticketsRes, conversationsRes, knowledgeRes] = await Promise.all([
        axios.get(`${API}/support/stats`).catch(() => ({ data: { tickets: { open: 0, in_progress: 0, resolved: 0, total: 0 }, conversations: { total: 0 } } })),
        axios.get(`${API}/support/tickets`).catch(() => ({ data: [] })),
        axios.get(`${API}/support/conversations`).catch(() => ({ data: [] })),
        axios.get(`${API}/support/knowledge`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data || null);
      setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
      setConversations(Array.isArray(conversationsRes.data) ? conversationsRes.data : []);
      setKnowledgeSources(Array.isArray(knowledgeRes.data) ? knowledgeRes.data : []);
    } catch (e) {
      console.error('Load error:', e);
      setStats({ tickets: { open: 0, in_progress: 0, resolved: 0, total: 0 }, conversations: { total: 0 } });
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}/status?status=${status}`);
      toast.success('Estado actualizado');
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status } : t));
    } catch (e) { toast.error('Error al actualizar'); }
  };

  const replyToTicket = async (ticketId, message) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}/respond?message=${encodeURIComponent(message)}&responder_type=admin`);
      toast.success('Respuesta enviada');
      const res = await axios.get(`${API}/support/tickets`).catch(() => ({ data: [] }));
      const updated = (res.data || []).find(t => t.id === ticketId);
      if (updated) {
        setTickets(prev => prev.map(t => t.id === ticketId ? updated : t));
        setSelectedTicket(updated);
      }
    } catch (e) { toast.error('Error al enviar respuesta'); }
  };

  const resolveTicket = async (ticketId, resolution) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}/resolve?resolution=${encodeURIComponent(resolution)}`);
      toast.success('Ticket marcado como resuelto');
      setSelectedTicket(prev => prev ? { ...prev, status: 'resolved' } : null);
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: 'resolved' } : t));
    } catch (e) { toast.error('Error al resolver ticket'); }
  };

  const addKnowledgeSource = async () => {
    if (!newKnowledge.name || !newKnowledge.content) { toast.error('Completa nombre y contenido'); return; }
    try {
      await axios.post(`${API}/support/knowledge`, newKnowledge);
      toast.success('Fuente agregada');
      setNewKnowledge({ name: '', source_type: 'url', content: '', agent_type: 'user' });
      loadData();
    } catch (e) { toast.error('Error al agregar fuente'); }
  };

  const deleteKnowledgeSource = async (id) => {
    try {
      await axios.delete(`${API}/support/knowledge/${id}`);
      toast.success('Fuente eliminada');
      loadData();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const sections = [
    { id: 'overview', label: 'Vista General', icon: Headphones },
    { id: 'tickets', label: 'Tickets', icon: AlertCircle, badge: newTicketCount > 0 ? newTicketCount : null },
    { id: 'conversations', label: 'Conversaciones', icon: MessageCircle },
    { id: 'knowledge', label: 'Base de Conocimiento', icon: BookOpen }
  ];

  return (
    <div className="space-y-6">
      {/* Notification Banner */}
      {!notificationsEnabled && 'Notification' in window && (
        <div className="glass-panel p-4 border border-yellow-500/30 bg-yellow-500/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BellRing className="text-yellow-400 flex-shrink-0" size={20} />
            <span className="text-yellow-100 text-sm">Activa las notificaciones para recibir alertas de nuevos tickets en tiempo real</span>
          </div>
          <button onClick={requestNotificationPermission} className="btn-primary text-sm px-4 py-2 flex items-center gap-2 flex-shrink-0">
            <Bell size={14} />Activar
          </button>
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-4 py-2 rounded-xl flex items-center gap-2 text-sm transition-colors relative ${
                activeSection === s.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Icon size={15} />
              {s.label}
              {s.badge && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {s.badge}
                </span>
              )}
            </button>
          );
        })}
        <button onClick={loadData} className="ml-auto px-3 py-2 rounded-xl bg-white/5 text-gray-500 hover:bg-white/10 hover:text-gray-300 transition-colors">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* ─── OVERVIEW ─── */}
      {activeSection === 'overview' && stats && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-white">Panel de Soporte</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: AlertCircle, color: 'text-red-400', label: 'Tickets Abiertos', value: stats.tickets?.open || 0 },
              { icon: Clock, color: 'text-blue-400', label: 'En Progreso', value: stats.tickets?.in_progress || 0 },
              { icon: Check, color: 'text-green-400', label: 'Resueltos', value: stats.tickets?.resolved || 0 },
              { icon: Users, color: 'text-yellow-400', label: 'Total Tickets', value: stats.tickets?.total || tickets.length },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="glass-panel p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={18} className={stat.color} />
                    <span className="text-gray-500 text-sm">{stat.label}</span>
                  </div>
                  <p className="text-3xl font-semibold text-white">{stat.value}</p>
                </div>
              );
            })}
          </div>

          {/* Breakdown by type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="glass-panel p-5">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Users size={16} className="text-blue-400" />Tickets de Usuarios
              </h3>
              <div className="space-y-2">
                {[
                  { label: 'Abiertos', value: tickets.filter(t => t.user_type === 'user' && t.status === 'open').length, color: 'text-yellow-400' },
                  { label: 'En Progreso', value: tickets.filter(t => t.user_type === 'user' && t.status === 'in_progress').length, color: 'text-blue-400' },
                  { label: 'Resueltos', value: tickets.filter(t => t.user_type === 'user' && t.status === 'resolved').length, color: 'text-green-400' },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-gray-500 text-sm">{row.label}</span>
                    <span className={`font-semibold ${row.color}`}>{row.value}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2">
                  <span className="text-gray-400 text-sm font-medium">Total usuarios</span>
                  <span className="text-white font-bold">{tickets.filter(t => t.user_type === 'user').length}</span>
                </div>
              </div>
            </div>

            <div className="glass-panel p-5">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Building2 size={16} className="text-purple-400" />Tickets de Negocios
              </h3>
              <div className="space-y-2">
                {[
                  { label: 'Abiertos', value: tickets.filter(t => t.user_type === 'business' && t.status === 'open').length, color: 'text-yellow-400' },
                  { label: 'En Progreso', value: tickets.filter(t => t.user_type === 'business' && t.status === 'in_progress').length, color: 'text-blue-400' },
                  { label: 'Resueltos', value: tickets.filter(t => t.user_type === 'business' && t.status === 'resolved').length, color: 'text-green-400' },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-gray-500 text-sm">{row.label}</span>
                    <span className={`font-semibold ${row.color}`}>{row.value}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2">
                  <span className="text-gray-400 text-sm font-medium">Total negocios</span>
                  <span className="text-white font-bold">{tickets.filter(t => t.user_type === 'business').length}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recent Tickets */}
            <div className="glass-panel p-5">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <AlertCircle size={16} className="text-red-400" />Tickets Recientes
              </h3>
              <div className="space-y-2">
                {tickets.slice(0, 5).map(t => (
                  <div
                    key={t.id}
                    className="p-3 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30 transition-colors"
                    onClick={() => { setSelectedTicket(t); setActiveSection('tickets'); }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-medium truncate flex-1">{t.subject}</span>
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs flex-shrink-0 ${STATUS_LABELS[t.status]?.bg} ${STATUS_LABELS[t.status]?.color}`}>
                        {STATUS_LABELS[t.status]?.label}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs">
                      {t.user_type === 'user' ? '👤' : '🏢'} {t.user_name || t.user_id} • {new Date(t.created_at).toLocaleDateString('es-MX')}
                    </p>
                  </div>
                ))}
                {tickets.length === 0 && <p className="text-gray-500 text-sm text-center py-4">Sin tickets</p>}
              </div>
            </div>

            {/* Recent Conversations */}
            <div className="glass-panel p-5">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <MessageCircle size={16} className="text-blue-400" />Conversaciones Recientes
              </h3>
              <div className="space-y-2">
                {conversations.slice(0, 5).map(c => (
                  <div
                    key={c.id}
                    className="p-3 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30 transition-colors"
                    onClick={() => { setSelectedConversation(c); setActiveSection('conversations'); }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-medium">{c.user_name || 'Usuario'}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.user_type === 'user' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                        {c.user_type === 'user' ? '👤' : '🏢'}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs">{c.messages?.length || 0} mensajes • {new Date(c.updated_at || c.created_at).toLocaleString('es-MX')}</p>
                  </div>
                ))}
                {conversations.length === 0 && <p className="text-gray-500 text-sm text-center py-4">Sin conversaciones</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── TICKETS ─── */}
      {activeSection === 'tickets' && (
        <TicketsCategoryPanel
          tickets={tickets}
          updateTicketStatus={updateTicketStatus}
          setSelectedTicket={setSelectedTicket}
          selectedTicket={selectedTicket}
          loadData={loadData}
          onReply={replyToTicket}
          onResolve={resolveTicket}
        />
      )}

      {/* ─── CONVERSATIONS ─── */}
      {activeSection === 'conversations' && (
        <ConversationsPanel
          conversations={conversations}
          selectedConversation={selectedConversation}
          setSelectedConversation={setSelectedConversation}
        />
      )}

      {/* ─── KNOWLEDGE BASE ─── */}
      {activeSection === 'knowledge' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Base de Conocimiento de Soporte</h2>
              <p className="text-gray-500 text-sm mt-1">Fuentes de información para el equipo de soporte</p>
            </div>
          </div>

          {/* Add Source */}
          <div className="glass-panel p-6">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Plus size={16} className="text-green-400" />Agregar Fuente de Conocimiento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                value={newKnowledge.name}
                onChange={(e) => setNewKnowledge({ ...newKnowledge, name: e.target.value })}
                placeholder="Nombre de la fuente"
                className="input-glass"
              />
              <select
                value={newKnowledge.agent_type}
                onChange={(e) => setNewKnowledge({ ...newKnowledge, agent_type: e.target.value })}
                className="input-glass"
              >
                <option value="user">Para Soporte de Usuarios</option>
                <option value="business">Para Soporte de Negocios</option>
                <option value="general">General (ambos)</option>
              </select>
            </div>
            <div className="flex gap-2 mb-4">
              {['url', 'text'].map(type => (
                <button
                  key={type}
                  onClick={() => setNewKnowledge({ ...newKnowledge, source_type: type })}
                  className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${
                    newKnowledge.source_type === type ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  {type === 'url' ? <><Link size={14} />URL</> : <><FileText size={14} />Texto</>}
                </button>
              ))}
            </div>
            <textarea
              value={newKnowledge.content}
              onChange={(e) => setNewKnowledge({ ...newKnowledge, content: e.target.value })}
              placeholder={newKnowledge.source_type === 'url' ? 'https://...' : 'Escribe el contenido de conocimiento para el soporte...'}
              className="input-glass w-full h-24 resize-none mb-4"
            />
            <button onClick={addKnowledgeSource} className="btn-primary flex items-center gap-2">
              <Plus size={16} />Agregar Fuente
            </button>
          </div>

          {/* Sources by type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['user', 'business'].map(agentType => (
              <div key={agentType} className="glass-panel p-5">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  {agentType === 'user'
                    ? <><Users size={16} className="text-blue-400" />Soporte Usuarios</>
                    : <><Building2 size={16} className="text-purple-400" />Soporte Negocios</>
                  }
                  <span className="ml-auto text-xs text-gray-500">
                    {knowledgeSources.filter(k => k.agent_type === agentType || k.agent_type === 'general').length} fuentes
                  </span>
                </h3>
                <div className="space-y-2">
                  {knowledgeSources
                    .filter(k => k.agent_type === agentType || k.agent_type === 'general')
                    .map(source => (
                      <div key={source.id} className="p-3 bg-black/20 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          {source.source_type === 'url'
                            ? <Link size={13} className="text-blue-400 flex-shrink-0" />
                            : <FileText size={13} className="text-green-400 flex-shrink-0" />
                          }
                          <span className="text-white text-sm truncate">{source.name}</span>
                          {source.agent_type === 'general' && (
                            <span className="text-xs text-yellow-500 flex-shrink-0">general</span>
                          )}
                        </div>
                        <button onClick={() => deleteKnowledgeSource(source.id)} className="p-1.5 hover:bg-red-500/20 rounded text-red-400 transition-colors flex-shrink-0">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))
                  }
                  {knowledgeSources.filter(k => k.agent_type === agentType || k.agent_type === 'general').length === 0 && (
                    <div className="text-center py-6">
                      <BookOpen size={24} className="text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Sin fuentes de conocimiento</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSupportPanel;
